import {
  clearNativeAccount,
  nativeResend,
  nativeResetPassword,
  nativeSignIn,
  nativeSignUp,
  nativeUpdateUser,
  nativeVerifyOtp,
} from "@/lib/azure/native-auth-client"

type Filter =
  | { type: "eq" | "gt"; column: string; value: unknown }
  | { type: "in"; column: string; value: unknown[] }
  | { type: "not"; column: string; operator: string; value: unknown }

type QuerySpec = {
  table: string
  operation: "select" | "insert" | "update" | "delete"
  columns?: string
  values?: unknown
  filters: Filter[]
  order?: { column: string; ascending: boolean }
  range?: { from: number; to: number }
  limit?: number
  or?: string
  single?: boolean
  maybeSingle?: boolean
  count?: string
  head?: boolean
  returning?: boolean
}

type CompatResult = {
  data: any
  error: { message: string; code?: string } | null
  count?: number | null
  status?: number
  statusText?: string
}

const uploadedFiles = new Map<string, string>()

/** Length of the one-time passcode issued by Microsoft Entra External ID. */
export const OTP_CODE_LENGTH = 8

function currentRole(): "company" | "job_seeker" {
  return window.location.pathname.includes("/employer") ? "company" : "job_seeker"
}

function externalLoginUrl(provider?: string): string {
  const params = new URLSearchParams({
    user_type: currentRole(),
    returnTo: `${window.location.pathname}${window.location.search}`,
  })
  if (provider === "google") params.set("provider", "google")
  if (window.location.pathname.includes("/signup") || provider === "google") {
    params.set("intent", "signup")
  }
  return `/api/auth/external/login?${params}`
}

export type AuthUser = {
  id: string
  email: string
  user_metadata: {
    user_type?: "company" | "job_seeker"
    full_name?: string
    company_name?: string
    [key: string]: unknown
  }
  app_metadata: { provider: string }
  aud: string
  created_at: string
}

export type AuthSession = {
  access_token: string
  refresh_token: string
  expires_in: number
  expires_at: number
  token_type: string
  user: AuthUser
}

async function currentSession() {
  const response = await fetch("/api/system/status", {
    credentials: "same-origin",
    cache: "no-store",
  })
  if (!response.ok) return null
  const body = await response.json()
  if (!body.user) return null

  const user = {
    id: body.user.id,
    email: body.user.email,
    user_metadata: {
      user_type: body.user.userType,
      full_name: body.user.name,
      company_name: body.user.name,
    },
    app_metadata: { provider: "entra_external_id" },
    aud: "authenticated",
    created_at: new Date(0).toISOString(),
  }
  return {
    access_token: "external-id-cookie-session",
    refresh_token: "",
    expires_in: 8 * 60 * 60,
    expires_at: Math.floor(Date.now() / 1000) + 8 * 60 * 60,
    token_type: "bearer",
    user,
  }
}

class AzureQueryBuilder implements PromiseLike<CompatResult> {
  private readonly spec: QuerySpec

  constructor(table: string) {
    this.spec = {
      table,
      operation: "select",
      filters: [],
    }
  }

  select(columns = "*", options?: { count?: string; head?: boolean }) {
    this.spec.columns = columns
    this.spec.count = options?.count
    this.spec.head = options?.head
    if (this.spec.operation !== "select") this.spec.returning = true
    return this
  }

  insert(values: unknown) {
    this.spec.operation = "insert"
    this.spec.values = values
    return this
  }

  update(values: unknown) {
    this.spec.operation = "update"
    this.spec.values = values
    return this
  }

  delete() {
    this.spec.operation = "delete"
    return this
  }

  eq(column: string, value: unknown) {
    this.spec.filters.push({ type: "eq", column, value })
    return this
  }

  gt(column: string, value: unknown) {
    this.spec.filters.push({ type: "gt", column, value })
    return this
  }

  in(column: string, value: unknown[]) {
    this.spec.filters.push({ type: "in", column, value })
    return this
  }

  not(column: string, operator: string, value: unknown) {
    this.spec.filters.push({ type: "not", column, operator, value })
    return this
  }

  or(expression: string) {
    this.spec.or = expression
    return this
  }

  order(column: string, options?: { ascending?: boolean }) {
    this.spec.order = { column, ascending: options?.ascending !== false }
    return this
  }

  range(from: number, to: number) {
    this.spec.range = { from, to }
    return this
  }

  limit(limit: number) {
    this.spec.limit = limit
    return this
  }

  single() {
    this.spec.single = true
    return this
  }

  maybeSingle() {
    this.spec.maybeSingle = true
    return this
  }

  async execute(): Promise<CompatResult> {
    try {
      const response = await fetch("/api/data/query", {
        method: "POST",
        credentials: "same-origin",
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(this.spec),
      })
      const body = await response.json().catch(() => ({}))
      if (!response.ok) {
        return {
          data: null,
          error: { message: body.error || response.statusText },
          count: body.count ?? null,
          status: response.status,
          statusText: response.statusText,
        }
      }
      return {
        data: body.data ?? null,
        error: null,
        count: body.count ?? null,
        status: response.status,
        statusText: response.statusText,
      }
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : "Azure data request failed.",
        },
        status: 0,
        statusText: "Network Error",
      }
    }
  }

  then<TResult1 = CompatResult, TResult2 = never>(
    onfulfilled?:
      | ((value: CompatResult) => TResult1 | PromiseLike<TResult1>)
      | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute().then(onfulfilled, onrejected)
  }
}

function uploadKind(bucket: string) {
  if (bucket === "cv-files") return "cv"
  if (bucket === "additional-documents") return "additional-document"
  if (bucket === "avatars") return "avatar"
  if (bucket === "company_logos" || bucket === "company-assets") return "logo"
  return null
}

class AzureStorageBucket {
  constructor(private readonly bucket: string) {}

  async upload(path: string, file: File, options?: { contentType?: string }) {
    const kind = uploadKind(this.bucket)
    if (!kind) {
      return { data: null, error: { message: "Unsupported storage bucket." } }
    }

    try {
      const request = await fetch("/api/storage/blob/upload-url", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          filename: file.name,
          contentType: options?.contentType || file.type,
          size: file.size,
        }),
      })
      const target = await request.json()
      if (!request.ok) throw new Error(target.error || "Upload could not be started.")

      const upload = await fetch(target.uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": options?.contentType || file.type,
        },
        body: file,
      })
      if (!upload.ok) throw new Error("Blob upload failed.")

      const completion = await fetch("/api/storage/blob/complete", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kind,
          container: target.container,
          blobName: target.blobName,
        }),
      })
      const completed = await completion.json()
      if (!completion.ok) throw new Error(completed.error || "Upload verification failed.")

      uploadedFiles.set(path, completed.id)
      return { data: { path }, error: null }
    } catch (error) {
      return {
        data: null,
        error: {
          message: error instanceof Error ? error.message : "Upload failed.",
        },
      }
    }
  }

  getPublicUrl(path: string) {
    const id = uploadedFiles.get(path)
    return {
      data: {
        publicUrl: id ? `/api/storage/blob/content?id=${encodeURIComponent(id)}` : path,
      },
    }
  }

  async remove(paths: string[]) {
    for (const path of paths) {
      const id = uploadedFiles.get(path)
      if (!id) continue
      const response = await fetch(
        `/api/storage/blob/read-url?id=${encodeURIComponent(id)}`,
        { method: "DELETE", credentials: "same-origin" },
      )
      if (!response.ok) {
        const result = await response.json().catch(() => ({}))
        return { data: null, error: { message: result.error || "Delete failed." } }
      }
      uploadedFiles.delete(path)
    }
    return { data: [], error: null }
  }

  async download(path: string) {
    const id = uploadedFiles.get(path)
    const response = await fetch(
      id ? `/api/storage/blob/content?id=${encodeURIComponent(id)}` : path,
      { credentials: "same-origin" },
    )
    if (!response.ok) {
      return { data: null, error: { message: "Download failed." } }
    }
    return { data: await response.blob(), error: null }
  }
}

export function createAzureCompatClient() {
  const redirectToExternalId = async (input?: { provider?: string }) => {
    window.location.assign(externalLoginUrl(input?.provider))
    return new Promise<never>(() => undefined)
  }

  return {
    from(table: string) {
      return new AzureQueryBuilder(table)
    },
    storage: {
      from(bucket: string) {
        return new AzureStorageBucket(bucket)
      },
      async listBuckets() {
        return {
          data: [
            { name: "cv-files" },
            { name: "additional-documents" },
            { name: "avatars" },
            { name: "company_logos" },
          ],
          error: null,
        }
      },
    },
    auth: {
      async getSession() {
        return { data: { session: await currentSession() }, error: null }
      },
      async getUser() {
        const session = await currentSession()
        return { data: { user: session?.user ?? null }, error: null }
      },
      async refreshSession() {
        return { data: { session: await currentSession() }, error: null }
      },
      onAuthStateChange(callback: (event: string, session: any) => void) {
        let active = true
        void currentSession().then(session => {
          if (active) callback("INITIAL_SESSION", session)
        })
        return {
          data: {
            subscription: {
              unsubscribe() {
                active = false
              },
            },
          },
        }
      },
      async signOut() {
        await clearNativeAccount()
        window.location.assign("/api/auth/external/logout")
        return { error: null }
      },
      signInWithPassword: nativeSignIn,
      signUp: nativeSignUp,
      signInWithOAuth: redirectToExternalId,
      resetPasswordForEmail: nativeResetPassword,
      resend: nativeResend,
      verifyOtp: nativeVerifyOtp,
      updateUser: nativeUpdateUser,
    },
  }
}

/** Browser-side data/auth/storage access for the Azure backend. */
export const azureDb: any = createAzureCompatClient()
